import { useCallback, useEffect, useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { IconButton } from "../../components/ui/ds";
import "./coach-bubble.css";

// v30/v35 — odkaz od kouča ako zatvárateľná bublina. Spúšťač je PRVÉ tlačidlo v ovládacej
// lište Dnes (vedľa emoji nálady) a bublina vychádza spod neho.
//
// Prečo je to rozdelené na hook + dve zložky: tlačidlo žije vnútri flex riadku s ostatnými
// ovládačmi, bublina musí byť POD celým riadkom v plnej šírke. Jeden komponent by to
// nedokázal bez absolútneho pozicovania, ktoré by sa na 360 px lámalo.
//
// Pravidlá stavu (jediné vstupy sú `day` a `msgKey`):
//   1. NOVÝ DEŇ → bublina je otvorená. Ráno je odkaz to prvé, čo má človek vidieť,
//      a včerajšie zatvorenie sa naň neprenáša.
//   2. TEN ISTÝ DEŇ, INÝ ODKAZ (kouč sa ozval znova — po vykonanej akcii, odmietnutí
//      alebo odškrtnutí návyku) → keď je bublina zatvorená, NEOTVÁRA sa sama, len na
//      tlačidle svieti „1" ako nová správa. Otvorenú bublinu nový odkaz proste nahradí.
//   3. INAK → drží sa posledný stav (zatvorené ostáva zatvorené).
//
// Stav prežíva reload aj prepnutie záložky (localStorage) — bez toho by sa bublina
// vracala pri každom načítaní a zatvorenie by nič neznamenalo.

const LS_KEY = "buzer_coach_bubble";

function readState() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; } catch { return {}; }
}
function writeState(v) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch { /* private mode */ }
}

export function useCoachBubble(day, msgKey) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(false);

  useEffect(() => {
    if (!msgKey) { setOpen(false); setUnread(false); return; }
    const st = readState();
    if (st.day !== day) {                       // 1. ráno (prvé zobrazenie v novom dni)
      setOpen(true); setUnread(false);
      writeState({ day, seen: msgKey, closed: false });
    } else if (st.seen !== msgKey) {            // 2. nový odkaz v ten istý deň
      if (st.closed) { setOpen(false); setUnread(true); }
      else { setOpen(true); writeState({ day, seen: msgKey, closed: false }); }
    } else {                                    // 3. ten istý odkaz — drž posledný stav
      setOpen(!st.closed); setUnread(false);
    }
  }, [day, msgKey]);

  // zatvorenie aj otvorenie zapisujú `seen` — po oboch je odkaz videný, takže „1" zhasne
  const close = useCallback(() => {
    setOpen(false); setUnread(false);
    writeState({ day, seen: msgKey, closed: true });
  }, [day, msgKey]);
  const toggle = useCallback(() => {
    setUnread(false);
    setOpen((v) => {
      writeState({ day, seen: msgKey, closed: v });
      return !v;
    });
  }, [day, msgKey]);

  return { open, unread, close, toggle, has: !!msgKey };
}

// Spúšťač v ovládacej lište. Biely, kým niet čo čítať; vo farbe témy (presne ako emoji
// nálady po zaznačení) pri novej správe — plus malá „1", nech je to čitateľné aj bez farby.
export function CoachSparkButton({ open, unread, toggle }) {
  return (
    <IconButton variant={unread ? "primary" : "neutral"} size="sm"
      className="bz-coachb__sparkbtn" aria-expanded={open}
      aria-label={unread ? "Nový odkaz od kouča" : open ? "Skryť odkaz od kouča" : "Zobraziť odkaz od kouča"}
      title={unread ? "Nový odkaz od kouča" : "Odkaz od kouča"} onClick={toggle}>
      <BuzerIcon name="sparkles" size={18}
        color={unread ? "var(--brand-on-primary)" : "var(--neutral-text-main)"} />
      {unread && <span className="bz-countbadge bz-coachb__badge">1</span>}
    </IconButton>
  );
}

// Samotná bublina — chvostík mieri hore doľava na tlačidlo v lište nad ňou.
export function CoachBubblePanel({ children, onClose }) {
  return (
    <div className="bz-coachb__bubble" role="status">
      <div className="bz-aitext">{children}</div>
      <button type="button" className="bz-coachb__close" aria-label="Zavrieť odkaz" onClick={onClose}>
        <BuzerIcon name="close" size={15} color="currentColor" />
      </button>
    </div>
  );
}
